import _ from "lodash";
import React, { useState } from "react";

import { useI18n } from "Hooks";
import classNames from "classnames";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";

import {
  getRatingParentTitle,
  getRatingHistoryDate,
} from "ClassProgressSummaryV2/modules/Utils";
import { colors, fontWeight } from "Constants/colorConstants";
import { getCriteriaValueFromCache } from "modules/CommonGraphqlHelpers";

import Thumbnail from "../Thumbnail";
import classes from "./HistoryList.scss";
import { Button, Dialog, IconButton, Tooltip } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { connect } from "react-redux";
import {
  getSelectedCourseId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  CAN_VIEW_PORTFOLIO_POSTS_FOR_CLASSES,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  MANAGE_GRADEBOOK_PERMISSION,
  PROGRESS_REPORTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
} from "Constants/permissionConstants";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";

const getHistoryItemStyle = ({ isPopUp, showBasicHistoryList, color }) => {
  if (showBasicHistoryList) {
    return {
      title: {
        fontSize: "1.4rem",
        color: colors.slate,
      },
      subTitle: {
        display: "none",
      },
      rating: {
        fontSize: "1.4rem",
        color: color ? color : "",
        fontWeight: fontWeight.AvenirNextBold,
      },
      itemStyle: {
        padding: "10px 0px",
      },
    };
  } else if (isPopUp) {
    return {
      title: {
        fontSize: "1.2rem",
      },
      subTitle: {
        fontSize: "1.0rem",
      },
      rating: {
        color: color ? color : "",
        fontSize: "1.2rem",
      },
    };
  } else {
    return {
      rating: {
        color: color ? color : "",
      },
    };
  }
};

const permParentTypeMap = {
  STUDENT_PROGRESS_REPORT: {
    perm: PROGRESS_REPORTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
    permType: "MODULE_SETTING",
  },
  POST: {
    perm: CAN_VIEW_PORTFOLIO_POSTS_FOR_CLASSES,
    permType: "MODULE_SETTING",
  },
  STUDENT_ASSIGNMENT: {
    perm: MANAGE_GRADEBOOK_PERMISSION,
    permType: "PERMISSION",
  },
};

const getCanSeeMore = ({ courseId, curriculumProgramId, parentType }) => {
  const isRnPEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);

  if (!isRnPEnabled) {
    return true;
  }

  const { perm, permType } = permParentTypeMap[parentType] || {};

  const contexts = generateRnPContextsByLevel({
    level: "COURSE",
    curriculumProgramId,
    courseId,
  });

  return permType === "MODULE_SETTING"
    ? ACLStorage.getModuleSettingsValueByContext(perm, _.first(contexts))
    : ACLStorage.getPermissionValueByContext(perm, _.first(contexts));
};

const getSeeMore = ({ parentType, parent, onClickHistoryItem, t }) => {
  switch (parentType) {
    case "STUDENT_PROGRESS_REPORT":
      return (
        <Button
          onClick={() => onClickHistoryItem({ parentType, parent })}
          variant={"inline-progressive"}
          size={"small"}
          className={classes.seeMore}
        >
          {t("common:open_with_label", { label: t("common:report_card") })}
        </Button>
      );
    case "STUDENT_ASSESSMENT_EVALUATION":
      return (
        <Button
          onClick={() => onClickHistoryItem({ parentType, parent })}
          variant={"inline-progressive"}
          size={"small"}
          className={classes.seeMore}
        >
          {t("common:open_with_label", {
            label: t("common:assessment_evaluation"),
          })}
        </Button>
      );
    case "POST":
      return (
        <Button
          onClick={() => onClickHistoryItem({ parentType, parent })}
          variant={"inline-progressive"}
          size={"small"}
          className={classes.seeMore}
        >
          {t("common:open_with_label", { label: t("common:portfolio_post") })}
        </Button>
      );
    case "STUDENT_ASSIGNMENT":
      return (
        <Button
          onClick={() => onClickHistoryItem({ parentType, parent })}
          variant={"inline-progressive"}
          size={"small"}
          className={classes.seeMore}
        >
          {t("common:open_with_label", {
            label: t("common:assessment_evaluation"),
          })}
        </Button>
      );
    default:
      return null;
  }
};

const deletableTypes = ["OBSERVATION"];

const DIALOG_INFO = {
  OBSERVATION: {
    title: "common:delete_with_label",
    label: "progressSummary:observation",
    message: "common:delete_msg_with_label",
    messageLabel: "common:record",
  },
};

const HistoryList = React.memo(props => {
  const {
    /*in basic version of history list, we dont show the thumbnail, subtitle
    and we show only black color for the rating given.*/
    isPopUp,
    ratingHistory,
    canRemoveRating,
    onClickHistoryItem,
    deleteRatingHistory,
    showThumbnail = true,
    showBasicHistoryList = false,
    userType,
    isAdminGradebook = false,
    isGradingPeriodUnlocked = true,
    canManageGradebook = true,
    courseId,
    curriculumProgramId,
  } = props;

  const { t } = useI18n(["common", "progressSummary"]);
  const [currentDialogItem, setCurrentDialogItem] = useState(null);

  const onCloseDeleteModal = () => {
    setCurrentDialogItem(null);
  };

  const onDeleteObservation = () => {
    deleteRatingHistory(currentDialogItem);
    setCurrentDialogItem(null);
  };

  const disabledLockedTermTooltip = (
    <div className={classes.tooltipContainer}>
      {t(
        "academicSetup:grades_for_grading_period_is_locked_observations_cannot_be_deleted"
      )}
    </div>
  );

  return (
    <div className={classes.container}>
      {currentDialogItem && (
        <Dialog
          variant="delete-dialog"
          title={t(DIALOG_INFO[_.get(currentDialogItem, "parentType")].title, {
            label: t(DIALOG_INFO[_.get(currentDialogItem, "parentType")].label),
          })}
          message={t(
            DIALOG_INFO[_.get(currentDialogItem, "parentType")].message,
            {
              label: t(
                DIALOG_INFO[_.get(currentDialogItem, "parentType")].messageLabel
              ),
            }
          )}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:delete")}
          onButtonTwoClick={onDeleteObservation}
          onButtonOneClick={onCloseDeleteModal}
          onClose={onCloseDeleteModal}
        />
      )}

      <div className={classes.itemsContainer}>
        {!_.isEmpty(ratingHistory) ? (
          _.map(
            ratingHistory,
            ({
              id,
              parentType,
              parent,
              publishedAt,
              academicCriteriaValue,
              createdBy: { firstName, lastName } = {},
            }) => {
              const creatorFullName = `${firstName} ${lastName}`;
              const { abbreviation, color } = getCriteriaValueFromCache(
                academicCriteriaValue
              );
              const historyItemStyle = getHistoryItemStyle({
                isPopUp,
                showBasicHistoryList,
                color,
              });
              const titleStyle = _.get(historyItemStyle, "title", {});
              const subTitleStyle = _.get(historyItemStyle, "subTitle", {});
              const ratingStyle = _.get(historyItemStyle, "rating", {});
              const itemStyle = _.get(historyItemStyle, "itemStyle", {});
              const isItemHovered =
                canRemoveRating && _.includes(deletableTypes, parentType);
              const itemStyles = classNames(classes.item, {
                [classes.itemHovered]: isItemHovered,
                [classes.itemTopAligned]: !showBasicHistoryList,
              });
              const thumbnailSize = isPopUp ? "24px" : null;

              const canSeeMore =
                !isPopUp &&
                _.isEqual(userType, "staff") &&
                getCanSeeMore({
                  courseId,
                  parentType,
                  curriculumProgramId,
                });

              return (
                <div className={itemStyles} style={itemStyle} key={id}>
                  {showThumbnail && (
                    <Thumbnail
                      parent={parent}
                      displayOn={"list"}
                      thumbnailSize={thumbnailSize}
                      parentType={parentType}
                      isPopUp={isPopUp}
                    />
                  )}

                  <div className={classes.middleItem}>
                    <div className={classes.title} style={titleStyle}>
                      {getRatingParentTitle({ parent, parentType })}
                    </div>
                    <div className={classes.subTitle} style={subTitleStyle}>
                      {!isPopUp
                        ? t("common:evaluated_by_with_label", {
                            name: creatorFullName,
                          })
                        : creatorFullName}
                      {` • ${getRatingHistoryDate({ publishedAt, t })}`}
                    </div>
                    {canSeeMore
                      ? getSeeMore({
                          parentType,
                          parent,
                          onClickHistoryItem,
                          t,
                        })
                      : null}
                  </div>
                  {!isAdminGradebook && canManageGradebook ? (
                    <ConditionalWrapper
                      condition={!isGradingPeriodUnlocked}
                      wrapper={
                        <Tooltip
                          tooltip={disabledLockedTermTooltip}
                          placement="left"
                        />
                      }
                      type={"wrapperAsElement"}
                    >
                      <div className={classes.deleteButtonContainer}>
                        <div className={classes.deleteButton}>
                          <IconButton
                            variant={"plain-subtle"}
                            size={"medium"}
                            onClick={() => {
                              setCurrentDialogItem({ id, parent, parentType });
                            }}
                            icon={<DeleteOutlined />}
                            disabled={!isGradingPeriodUnlocked}
                          />
                        </div>
                      </div>
                    </ConditionalWrapper>
                  ) : null}

                  <div className={classes.rating} style={ratingStyle}>
                    {abbreviation || "-"}
                  </div>
                </div>
              );
            }
          )
        ) : (
          <div className={classes.currentRatingEmptyValue}>{`-`}</div>
        )}
      </div>
    </div>
  );
});

HistoryList.displayName = "HistoryList";

const mapStateToProps = state => {
  const courseId = getSelectedCourseId(state);
  const curriculumProgramId = getSelectedCurriculumId(state);

  return {
    courseId,
    curriculumProgramId,
  };
};

export default connect(mapStateToProps)(HistoryList);
